import { Module, GetterTree, MutationTree, ActionTree } from "vuex";
import { RootState } from "@/store/types";
import {
  CreateScheduleTransferRequest,
  ScheduleTransfer,
  ScheduleTransferService,
  UpdateScheduleTransferStatusRequest,
} from "@/api";

type ScheduleTransferState = {
  schedules: ScheduleTransfer[] | null;
};

const state: ScheduleTransferState = {
  schedules: null,
};

const getters: GetterTree<ScheduleTransferState, RootState> = {
  schedules: state => state.schedules,
};

const mutations: MutationTree<ScheduleTransferState> = {
  SET_SCHEDULES(state, schedules: ScheduleTransfer[]) {
    state.schedules = schedules;
  },
  ADD_SCHEDULE(state, schedule: ScheduleTransfer) {
    if (state.schedules == null) {
      state.schedules = [];
    }
    state.schedules.push(schedule);
  },
  UPDATE_SCHEDULE(state, updatedSchedule: ScheduleTransfer) {
    if (state.schedules) {
      const index = state.schedules.findIndex(
        schedule => schedule.id === updatedSchedule.id,
      );
      if (index !== -1) {
        state.schedules.splice(index, 1, updatedSchedule);
      }
    }
  },
  REMOVE_SCHEDULE(state, scheduleId: number) {
    if (state.schedules) {
      const index = state.schedules.findIndex(
        schedule => schedule.id === scheduleId,
      );
      if (index !== -1) {
        state.schedules.splice(index, 1);
      }
    }
  },
};

const actions: ActionTree<ScheduleTransferState, RootState> = {
  async fetchSchedules({ commit, rootState }) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const schedules = await ScheduleTransferService.getScheduleTransfers({
        companyId,
      });
      commit("SET_SCHEDULES", schedules);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async addSchedule(
    { commit, rootState },
    schedule: CreateScheduleTransferRequest,
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const newSchedule = await ScheduleTransferService.addScheduleTransfer({
        companyId,
        requestBody: schedule,
      });
      commit("ADD_SCHEDULE", newSchedule);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async updateScheduleStatus(
    { commit, rootState },
    {
      schedule,
      scheduleId,
      scheduleTransferId,
    }: {
      schedule: UpdateScheduleTransferStatusRequest;
      scheduleId: number;
      scheduleTransferId: number;
    },
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const updatedSchedule = await ScheduleTransferService.updateScheduleTransferStatus(
        {
          companyId,
          scheduleId,
          scheduleTransferId,
          requestBody: schedule,
        },
      );
      commit("UPDATE_SCHEDULE", updatedSchedule);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async removeSchedule(
    { commit, rootState },
    {
      scheduleId,
      scheduleTransferId,
    }: { scheduleId: number; scheduleTransferId: number },
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      await ScheduleTransferService.deleteScheduleTransfer({
        companyId,
        scheduleId,
        scheduleTransferId,
      });
      commit("REMOVE_SCHEDULE", scheduleTransferId);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
};

export const scheduleTransfer: Module<ScheduleTransferState, RootState> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
